// /mfa-legalisation/documents/$group — Wave 63: TH deep-dive per document family
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import type { MfaDocGroup } from "@/data/mfa-doc-groups";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/mfa-legalisation/documents/$group")({
  loader: async ({ params }) => {
    const { MFA_DOC_GROUPS, getMfaDocGroup } = await import("@/data/mfa-doc-groups");
    const group = getMfaDocGroup(params.group);
    if (!group) throw notFound();
    return {
      __d: { MFA_DOC_GROUPS, getMfaDocGroup }, group };
  },
  head: ({ params, loaderData }) => {
    const g = loaderData?.group as MfaDocGroup | undefined;
    if (!g) return {};
    const path = `/mfa-legalisation/documents/${params.group}`;
    const url = `${SITE}${path}`;
    const enUrl = `${SITE}/en/mfa-legalisation/documents/${params.group}`;
    const title = `${g.titleTh} — รับรองกรมการกงสุลและสถานทูต | NYC`;
    const desc = g.summaryTh.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, path, "th") },
        { name: "description", content: ctaDesc(desc, path, "th") },
        { property: "og:title", content: ctaTitle(title, path, "th") },
        { property: "og:description", content: ctaDesc(desc, path, "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "รับรองเอกสาร MFA + สถานทูต", item: `${SITE}/mfa-legalisation` },
              { "@type": "ListItem", position: 2, name: "เอกสารที่ต้องรับรอง", item: `${SITE}/mfa-legalisation/documents` },
              { "@type": "ListItem", position: 3, name: g.titleTh, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            inLanguage: "th",
            mainEntity: g.faqsTh.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: MfaDocGroupPage,
});

function MfaDocGroupPage() {
  const { __d: { MFA_DOC_GROUPS, getMfaDocGroup } } = Route.useLoaderData() as { __d: typeof import("@/data/mfa-doc-groups") };
  const { group: g } = Route.useLoaderData() as { group: MfaDocGroup };
  const others = MFA_DOC_GROUPS.filter((x) => x.slug !== g.slug);

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/mfa-legalisation" className="hover:underline">
          รับรองเอกสาร MFA + สถานทูต
        </Link>{" "}
        /{" "}
        <Link to="/mfa-legalisation/documents" className="hover:underline">
          เอกสารที่ต้องรับรอง
        </Link>{" "}
        / {g.titleTh}
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{g.titleTh}</h1>
      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        {g.summaryTh}
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>สถานะ Apostille ของไทย:</strong> อนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่{" "}
        {APOSTILLE_STATUS.entryIntoForceTh} ก่อนถึงวันดังกล่าวเอกสารยังต้องผ่านกรมการกงสุลและสถานทูตปลายทาง{" "}
        <a href={APOSTILLE_STATUS.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="underline">
          ที่มา: HCCH
        </a>
      </aside>

      <div className="mt-8 space-y-4 text-base leading-8">
        {g.bodyTh.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <h2 className="mt-10 text-2xl font-semibold tracking-tight">เอกสารในกลุ่มนี้</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.documentsTh.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">ลำดับขั้นตอน</h2>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-7">
        {g.chainTh.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">เช็กลิสต์ก่อนดำเนินการ</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.checklistTh.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">คำถามที่พบบ่อย</h2>
      <div className="mt-2 space-y-3">
        {g.faqsTh.map((f) => (
          <div key={f.q} className="rounded-lg border border-border p-4">
            <p className="font-medium voice-faq-q">{f.q}</p>
            <p className="mt-1 text-sm leading-7 text-muted-foreground voice-faq-a">{f.a}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-10 text-xl font-semibold tracking-tight">กลุ่มเอกสารอื่น</h2>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
        {others.map((o) => (
          <li key={o.slug}>
            <Link to="/mfa-legalisation/documents/$group" params={{ group: o.slug }} className="text-primary hover:underline">
              {o.titleTh}
            </Link>
          </li>
        ))}
      </ul>
          <ServiceAuthorityBlock topic="mfa" locale="th" />
    </main>
  );
}
